import { useRef, useMemo, useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { switchAccount } from 'redux/features/auth/authSlice'
import { isFailed, isSuccess } from 'redux/toolkit/api'
import { SwitchAccount } from 'redux/features/auth/authApiThunks'

// C1: DEAD CODE - This entire hook is SOAP-only
// Used by AccountSwitcherEmailProtectionSingle for SOAP session account switching
// Login App sessions use OAuth2 redirect flow instead (handled in the component)
// Safe to delete when SOAP sessions are fully deprecated
export const useSwitchAccountAsync = () => {
  const dispatch = useAppDispatch()
  const { accountSwitchIsSuccess, accountSwitchIsFailed, switchAccountResponse } = useAppSelector(_store => ({
    accountSwitchIsSuccess: isSuccess(_store.auth.api.switchAccountApiStatus),
    accountSwitchIsFailed: isFailed(_store.auth.api.switchAccountApiStatus),
    switchAccountResponse: _store.auth.switchAccount
  }))

  // Promise handler functions to be called in the redux state listener effects
  const resolvePromise = useRef<((result: SwitchAccount) => void) | null>()
  const rejectPromise = useRef<((reason: string | Error) => void) | null>()

  const switchAccountLocal = useCallback(
    (newAccountId: string): Promise<SwitchAccount> =>
      new Promise((resolve, reject) => {
        if (rejectPromise.current) {
          // Abort previous execution if a new one is triggered concurrently
          rejectPromise.current('abort')
        }

        // Capture new promise handler functions to be used in the effects
        resolvePromise.current = resolve
        rejectPromise.current = reject

        // Dispatch the switch account redux action
        dispatch(switchAccount(newAccountId))
      }),
    [dispatch]
  )

  // Listen for redux API thunk success, resolve pending promise
  useEffect(() => {
    if (!accountSwitchIsSuccess || !switchAccountResponse) {
      return
    }
    resolvePromise.current?.(switchAccountResponse)
    resolvePromise.current = null
    rejectPromise.current = null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSwitchIsSuccess])

  // Listen for redux API thunk error, reject pending promise
  useEffect(() => {
    if (!accountSwitchIsFailed) {
      return
    }
    rejectPromise.current?.('Switch account failed')
    resolvePromise.current = null
    rejectPromise.current = null
  }, [accountSwitchIsFailed])

  return useMemo(() => switchAccountLocal, [switchAccountLocal])
}
